/**
 * ============================================================================
 * THE ONLY FILE IN THIS REPO THAT IS ALLOWED TO IMPORT `peerjs`.
 * ============================================================================
 *
 * Everything else talks to the `Transport` interface. When V2 replaces this
 * with raw RTCPeerConnection (for a genuinely unreliable input channel), this
 * file is the diff.
 *
 * Two data channels per peer, both opened by the guest:
 *
 *   retro-ctl   reliable: true,  serialization 'json'  -> ordered, retransmitted
 *   retro-in    reliable: false, serialization 'raw'   -> UNORDERED, still retransmitted
 *
 * On that second one: PeerJS builds the channel as
 *   `peerConnection.createDataChannel(label, { ordered: !!options.reliable })`
 * and sets neither `maxRetransmits` nor `maxPacketLifeTime`. So `reliable:false`
 * buys unordered delivery only — SCTP still retransmits forever. That is fine
 * for V1 (unordered is the property input actually needs, so one late packet
 * cannot head-of-line block the ones behind it) but it is NOT the `maxRetransmits: 0`
 * channel that rollback wants. Verified at runtime, see `describeChannels()`.
 *
 * Cost of the second channel: PeerJS calls `_startPeerConnection()` per
 * connection, so each peer costs two RTCPeerConnections, and three once voice
 * is attached, rather than one PC carrying three channels. Accepted for V1;
 * V2's raw adapter collapses it.
 *
 * Voice rides that third connection as an ordinary PeerJS MediaConnection. It
 * carries no game state and nothing in the lockstep path waits on it, so a
 * denied microphone or a dead audio track costs conversation and never a frame.
 */
import { Peer } from 'peerjs';
import type { DataConnection, MediaConnection, PeerOptions } from 'peerjs';

import { Signal } from './emitter.js';
import type { Unsubscribe } from './emitter.js';
import { decodeControl } from './protocol.js';
import type { ControlMessage } from './protocol.js';
import { generateRoomCode, hostPeerId } from './room-code.js';
import type {
  BrokerConfig,
  ChannelDiagnostics,
  ConnectResult,
  PeerId,
  PeerInfo,
  PeerStats,
  Transport,
  TransportError,
  TransportOptions,
  TransportRole,
  TransportStatus,
  VoiceDiagnostics,
} from './transport.js';

const CONTROL_LABEL = 'retro-ctl';
const INPUT_LABEL = 'retro-in';

/** PeerJS 1.5 serializer keys. Note: the "no envelope" key is 'raw', NOT 'none'. */
const CONTROL_SERIALIZATION = 'json';
const INPUT_SERIALIZATION = 'raw';

const DEFAULT_CONNECT_TIMEOUT_MS = 20_000;
/** How many times to roll a fresh room code when the broker ID is squatted. */
const ID_CLAIM_ATTEMPTS = 4;

/** How long to wait after a voice call drops before re-forming it. */
const MEDIA_REDIAL_MS = 400;
/** Stops a voice call that will never form from retrying forever. */
const MEDIA_CALL_ATTEMPTS = 6;

/** PeerJS error types that leave the Peer unusable. */
const FATAL_PEER_ERRORS = new Set([
  'browser-incompatible',
  'invalid-id',
  'invalid-key',
  'ssl-unavailable',
  'server-error',
  'socket-error',
  'socket-closed',
  'unavailable-id',
]);

interface PeerRecord {
  info: PeerInfo;
  control: DataConnection | null;
  input: DataConnection | null;
  media: MediaConnection | null;
  /** What `media` is carrying outbound, so a changed microphone is detectable. */
  mediaStream: MediaStream | null;
  /** Bounds the re-dial loop in `#adoptCall`. Reset once audio flows. */
  callAttempts: number;
  announced: boolean;
}

interface PeerJsError {
  type?: string;
  message?: string;
}

export class PeerJsTransport implements Transport {
  readonly role: TransportRole;

  #roomCode: string;
  #label: string;
  #broker: BrokerConfig;
  #connectTimeoutMs: number;

  #peer: Peer | null = null;
  #selfId: PeerId | null = null;
  #closed = false;
  /**
   * Set while a guest is dialling. PeerJS reports a nonexistent room as a
   * `peer-unavailable` error on the *Peer*, and leaves the pending
   * DataConnection open forever, so without this hook the guest would sit
   * through the full connect timeout instead of failing in about a second.
   */
  #dialFailure: ((err: Error) => void) | null = null;

  #records = new Map<PeerId, PeerRecord>();
  /** Host: the stream to offer every peer, including ones that join later. */
  #outboundStream: MediaStream | null = null;
  /** What the senders should be transmitting. Null while muted. */
  #outboundTrack: MediaStreamTrack | null = null;

  readonly #peerJoin = new Signal<[PeerInfo]>();
  readonly #peerLeave = new Signal<[PeerId, string]>();
  readonly #input = new Signal<[PeerId, Uint8Array, number]>();
  readonly #control = new Signal<[PeerId, ControlMessage]>();
  readonly #stream = new Signal<[PeerId, MediaStream]>();
  readonly #status = new Signal<[TransportStatus, string]>();
  readonly #error = new Signal<[TransportError]>();

  constructor(options: TransportOptions) {
    this.role = options.role;
    this.#roomCode = options.roomCode;
    this.#label = options.label ?? options.role;
    this.#broker = options.broker;
    this.#connectTimeoutMs = options.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS;
  }

  get roomCode(): string {
    return this.#roomCode;
  }

  get selfId(): PeerId | null {
    return this.#selfId;
  }

  // -- lifecycle ------------------------------------------------------------

  async connect(): Promise<ConnectResult> {
    if (this.#peer) throw new Error('connect() already called on this transport');
    this.#status.emit('connecting', this.role === 'host' ? 'claiming room code' : 'dialing host');
    try {
      const result =
        this.role === 'host' ? await this.#connectAsHost() : await this.#connectAsGuest();
      this.#status.emit('ready', `connected as ${result.selfId}`);
      return result;
    } catch (err) {
      this.#status.emit('error', errorMessage(err));
      throw err;
    }
  }

  async #connectAsHost(): Promise<ConnectResult> {
    let regenerated = false;
    for (let attempt = 1; attempt <= ID_CLAIM_ATTEMPTS; attempt += 1) {
      try {
        const selfId = await this.#openPeer(hostPeerId(this.#roomCode));
        return { selfId, roomCode: this.#roomCode, regenerated };
      } catch (err) {
        // Gotcha #3: custom IDs on the public broker are first-come-first-served.
        // Someone else holding this one is not fatal, it just means roll again.
        if (!isUnavailableId(err) || attempt === ID_CLAIM_ATTEMPTS || this.#closed) throw err;
        this.#teardownPeer();
        this.#roomCode = generateRoomCode();
        regenerated = true;
        this.#status.emit('connecting', `room code was taken, rolled a new one (attempt ${attempt + 1})`);
      }
    }
    /* c8 ignore next */
    throw new Error('unreachable');
  }

  async #connectAsGuest(): Promise<ConnectResult> {
    const selfId = await this.#openPeer(undefined);
    const target = hostPeerId(this.#roomCode);

    // Both channels are dialled at once. The control channel opening is what
    // counts as "joined"; input can lag a beat without blocking anything,
    // because input is redundant by design.
    const control = this.#peer!.connect(target, {
      label: CONTROL_LABEL,
      serialization: CONTROL_SERIALIZATION,
      reliable: true,
      metadata: { label: this.#label },
    });
    const input = this.#peer!.connect(target, {
      label: INPUT_LABEL,
      serialization: INPUT_SERIALIZATION,
      reliable: false,
      metadata: { label: this.#label },
    });

    const record = this.#recordFor(target);
    this.#adoptControl(record, control);
    this.#adoptInput(record, input);

    const dialRejected = new Promise<never>((_, reject) => {
      this.#dialFailure = reject;
    });
    try {
      await Promise.race([
        this.#awaitOpen(control, `host ${target} did not accept the control channel`),
        dialRejected,
      ]);
    } finally {
      this.#dialFailure = null;
    }
    return { selfId, roomCode: this.#roomCode, regenerated: false };
  }

  /** Creates the Peer and resolves with the broker-assigned ID. */
  #openPeer(requestedId: string | undefined): Promise<PeerId> {
    const options = buildPeerOptions(this.#broker);
    const peer = requestedId ? new Peer(requestedId, options) : new Peer(options);
    this.#peer = peer;

    return new Promise<PeerId>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new TransportFailure('broker-timeout', `broker did not respond in ${this.#connectTimeoutMs}ms`));
      }, this.#connectTimeoutMs);

      peer.on('open', (id) => {
        this.#selfId = id;
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(id);
      });

      peer.on('error', (raw: PeerJsError) => {
        const type = raw?.type ?? 'unknown';
        const message = raw?.message ?? String(raw);
        const fatal = FATAL_PEER_ERRORS.has(type);
        // The room does not exist. Fail the guest's dial now rather than
        // letting it wait out the connect timeout.
        if (type === 'peer-unavailable' && this.#dialFailure) {
          const fail = this.#dialFailure;
          this.#dialFailure = null;
          fail(new TransportFailure(type, message));
          return;
        }
        if (!settled && fatal) {
          settled = true;
          clearTimeout(timer);
          reject(new TransportFailure(type, message));
          return;
        }
        this.#error.emit({ code: type, message, fatal });
        if (fatal) this.#status.emit('error', `${type}: ${message}`);
      });

      peer.on('disconnected', () => {
        if (this.#closed) return;
        this.#status.emit('reconnecting', 'lost the broker socket, reconnecting');
        // Gotcha #4: the public broker drops sockets. Losing the broker does not
        // drop live WebRTC connections, but it does stop new peers joining.
        try {
          peer.reconnect();
        } catch (err) {
          this.#error.emit({ code: 'reconnect-failed', message: errorMessage(err), fatal: false });
        }
      });

      peer.on('close', () => {
        if (this.#closed) return;
        this.#status.emit('closed', 'broker connection closed');
      });

      // Every peer accepts connections, not just the host: in a mesh a guest is
      // dialled by its fellow guests too.
      peer.on('connection', (conn) => this.#onIncomingData(conn));
      peer.on('call', (call) => this.#onIncomingCall(call));
    });
  }

  dial(peerId: PeerId): void {
    if (this.#closed || !this.#peer || peerId === this.#selfId) return;
    const record = this.#recordFor(peerId);
    // Already connected, or a dial is already in flight.
    if (record.control) return;
    const metadata = { label: this.#label };
    this.#adoptControl(
      record,
      this.#peer.connect(peerId, {
        label: CONTROL_LABEL,
        serialization: CONTROL_SERIALIZATION,
        reliable: true,
        metadata,
      }),
    );
    this.#adoptInput(
      record,
      this.#peer.connect(peerId, {
        label: INPUT_LABEL,
        serialization: INPUT_SERIALIZATION,
        reliable: false,
        metadata,
      }),
    );
  }

  disconnectPeer(peerId: PeerId, reason = 'disconnected by local peer'): void {
    const record = this.#records.get(peerId);
    if (!record) return;
    if (record.control?.open) {
      try {
        record.control.send({ t: 'bye', reason } satisfies ControlMessage);
      } catch {
        /* best effort */
      }
    }
    this.#dropRecord(peerId, reason, true);
  }

  close(reason = 'closed by local peer'): void {
    if (this.#closed) return;
    this.#closed = true;
    for (const [id, record] of this.#records) {
      if (record.control?.open) {
        try {
          record.control.send({ t: 'bye', reason } satisfies ControlMessage);
        } catch {
          /* best effort */
        }
      }
      this.#dropRecord(id, reason, false);
    }
    this.#teardownPeer();
    this.#outboundStream = null;
    this.#status.emit('closed', reason);
    this.#peerJoin.clear();
    this.#peerLeave.clear();
    this.#input.clear();
    this.#control.clear();
    this.#stream.clear();
    this.#error.clear();
    this.#status.clear();
  }

  #teardownPeer(): void {
    const peer = this.#peer;
    this.#peer = null;
    if (!peer) return;
    peer.removeAllListeners();
    if (!peer.destroyed) peer.destroy();
  }

  // -- incoming connections -------------------------------------------------

  #onIncomingData(conn: DataConnection): void {
    const record = this.#recordFor(conn.peer);
    const meta = conn.metadata as { label?: string } | undefined;
    if (typeof meta?.label === 'string') record.info.label = meta.label;

    if (conn.label === CONTROL_LABEL) {
      this.#adoptControl(record, conn);
    } else if (conn.label === INPUT_LABEL) {
      this.#adoptInput(record, conn);
    } else {
      this.#error.emit({
        code: 'unknown-channel',
        message: `peer opened an unrecognised channel "${conn.label}"`,
        fatal: false,
        peerId: conn.peer,
      });
      conn.close();
    }
  }

  #onIncomingCall(call: MediaConnection): void {
    const record = this.#recordFor(call.peer);
    // Only one end ever dials, so a second call to a peer we already have is
    // something going wrong rather than something to adopt: taking it would
    // orphan the connection already carrying audio.
    if (record.media) {
      call.close();
      return;
    }
    record.media = call;
    // Answering *with* our own stream is what makes voice two-way over a single
    // MediaConnection: our track rides the answer, theirs rode the offer, and
    // both ends get a 'stream' event. Undefined when we have no microphone —
    // that peer then hears everyone without being heard.
    const stream = this.#outboundStream;
    record.mediaStream = stream;
    // Answering *with* a stream is what makes voice two-way over a single
    // MediaConnection, and what makes the m-line sendrecv rather than recvonly.
    call.answer(stream ?? undefined);
    this.#adoptCall(record, call);
    this.#applyTrack(record);
  }

  #adoptControl(record: PeerRecord, conn: DataConnection): void {
    record.control?.close();
    record.control = conn;

    conn.on('open', () => {
      if (!record.announced) {
        record.announced = true;
        this.#peerJoin.emit(record.info);
      }
      this.#watchConnectionState(conn);
      // A peer that arrives after we already have a microphone still gets it.
      this.#offerVoice(record.info.id);
    });
    conn.on('data', (raw) => {
      const msg = decodeControl(raw);
      if (!msg) {
        this.#error.emit({
          code: 'bad-control-frame',
          message: 'dropped an undecodable control message',
          fatal: false,
          peerId: conn.peer,
        });
        return;
      }
      this.#control.emit(conn.peer, msg);
    });
    conn.on('close', () => this.#dropRecord(conn.peer, 'control channel closed', true));
    conn.on('error', (err: PeerJsError) =>
      this.#error.emit({
        code: err?.type ?? 'control-error',
        message: err?.message ?? String(err),
        fatal: false,
        peerId: conn.peer,
      }),
    );
  }

  #adoptInput(record: PeerRecord, conn: DataConnection): void {
    record.input?.close();
    record.input = conn;

    conn.on('data', (raw) => {
      const receivedAt = performance.now();
      const bytes = toBytes(raw);
      if (!bytes) {
        this.#error.emit({
          code: 'bad-input-frame',
          message: `input channel delivered a ${describeType(raw)}, expected binary`,
          fatal: false,
          peerId: conn.peer,
        });
        return;
      }
      this.#input.emit(conn.peer, bytes, receivedAt);
    });
    conn.on('close', () => {
      if (record.input === conn) record.input = null;
    });
    conn.on('error', (err: PeerJsError) =>
      this.#error.emit({
        code: err?.type ?? 'input-error',
        message: err?.message ?? String(err),
        fatal: false,
        peerId: conn.peer,
      }),
    );
  }

  /**
   * A browser tab that is killed outright does not always get to close its data
   * channels politely, and PeerJS only surfaces `close` when the channel itself
   * closes. Watching the RTCPeerConnection turns a ~30s ICE timeout into a
   * prompt, definite answer — which matters a great deal to a lockstep game,
   * where a peer nobody has declared dead is a peer everybody waits for.
   */
  #watchConnectionState(conn: DataConnection): void {
    const pc = conn.peerConnection;
    if (!pc) return;
    const onChange = (): void => {
      const state = pc.connectionState;
      if (state === 'failed' || state === 'closed') {
        this.#dropRecord(conn.peer, `webrtc connection ${state}`, true);
      } else if (state === 'disconnected') {
        // Often transient; report it but do not act on it.
        this.#error.emit({
          code: 'peer-disconnected',
          message: 'webrtc connection disconnected, may recover',
          fatal: false,
          peerId: conn.peer,
        });
      }
    };
    pc.addEventListener('connectionstatechange', onChange);
    pc.addEventListener('iceconnectionstatechange', () => {
      if (pc.iceConnectionState === 'failed') {
        this.#dropRecord(conn.peer, 'ice connection failed', true);
      }
    });
  }

  #recordFor(peerId: PeerId): PeerRecord {
    let record = this.#records.get(peerId);
    if (!record) {
      record = {
        info: { id: peerId, label: peerId, joinedAt: Date.now() },
        control: null,
        input: null,
        media: null,
        mediaStream: null,
        callAttempts: 0,
        announced: false,
      };
      this.#records.set(peerId, record);
    }
    return record;
  }

  #dropRecord(peerId: PeerId, reason: string, announce: boolean): void {
    const record = this.#records.get(peerId);
    if (!record) return;
    this.#records.delete(peerId);
    record.input?.close();
    record.control?.close();
    record.media?.close();
    if (announce && record.announced) this.#peerLeave.emit(peerId, reason);
  }

  #awaitOpen(conn: DataConnection, timeoutMessage: string): Promise<void> {
    if (conn.open) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        reject(new TransportFailure('connect-timeout', timeoutMessage));
      }, this.#connectTimeoutMs);
      const onOpen = (): void => {
        cleanup();
        resolve();
      };
      const onFail = (err?: PeerJsError): void => {
        cleanup();
        reject(new TransportFailure(err?.type ?? 'connection-closed', err?.message ?? timeoutMessage));
      };
      const cleanup = (): void => {
        clearTimeout(timer);
        conn.off('open', onOpen);
        conn.off('error', onFail);
        conn.off('close', onFail);
      };
      conn.on('open', onOpen);
      conn.on('error', onFail);
      conn.on('close', onFail);
    });
  }

  // -- sending --------------------------------------------------------------

  sendControl(msg: ControlMessage, to?: PeerId): void {
    for (const record of this.#targets(to)) {
      if (!record.control?.open) continue;
      try {
        record.control.send(msg);
      } catch (err) {
        this.#error.emit({
          code: 'control-send-failed',
          message: errorMessage(err),
          fatal: false,
          peerId: record.info.id,
        });
      }
    }
  }

  async whenInputOpen(to: PeerId): Promise<void> {
    const deadline = Date.now() + this.#connectTimeoutMs;
    // The record's input slot is filled when the guest's dial arrives, so early
    // on there may be nothing to even wait on. Poll for the connection to
    // exist, then wait on its own open event.
    for (;;) {
      const conn = this.#records.get(to)?.input;
      if (conn) return this.#awaitOpen(conn, `input channel to ${to} never opened`);
      if (Date.now() > deadline) {
        throw new TransportFailure('connect-timeout', `input channel to ${to} never appeared`);
      }
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  sendInput(payload: Uint8Array, to?: PeerId): void {
    for (const record of this.#targets(to)) {
      // Never queue, never retransmit: gotcha #5. A packet that cannot go out
      // right now is already stale, and the next one carries its frames anyway.
      if (!record.input?.open) continue;
      try {
        record.input.send(payload);
      } catch {
        /* dropped on purpose */
      }
    }
  }

  /**
   * Establish voice with every peer using this stream. Called once, at join.
   *
   * The stream is a silent placeholder, not the microphone — see the interface.
   * All that matters here is that a track exists, so every call negotiates
   * `sendrecv` and has somewhere to put a microphone later.
   */
  attachStream(stream: MediaStream): void {
    this.#outboundStream = stream;
    for (const record of this.#records.values()) {
      if (!record.media) this.#offerVoice(record.info.id);
    }
  }

  /**
   * Swap what we are transmitting, on every call at once. Null sends nothing.
   *
   * The whole mute/unmute story is this one line of `replaceTrack`. It needs no
   * renegotiation, so the call is untouched and a toggle is instant — which is
   * exactly why `attachStream` goes to the trouble of establishing `sendrecv`
   * up front, even with nothing to say yet.
   */
  setOutboundTrack(track: MediaStreamTrack | null): void {
    this.#outboundTrack = track;
    for (const record of this.#records.values()) this.#applyTrack(record);
  }

  #applyTrack(record: PeerRecord): void {
    const call = record.media;
    if (!call) return;
    const sender = this.#audioSender(call);
    if (!sender) return;
    void sender.replaceTrack(this.#outboundTrack).catch((err: unknown) =>
      this.#error.emit({
        code: 'replace-track-failed',
        message: errorMessage(err),
        fatal: false,
        peerId: record.info.id,
      }),
    );
  }

  /**
   * The sender that can actually carry our microphone on a call.
   *
   * Direction is the whole point, and `getSenders()` will lie to you about it.
   * A recvonly transceiver still exposes a sender, with a null track, that
   * accepts `replaceTrack()` and reports success while transmitting precisely
   * nothing. Every call here is established with a track so this should always
   * find one; returning null rather than that phantom sender is what keeps a
   * failure loud instead of silent.
   *
   * `currentDirection` is what was negotiated and null until the answer lands;
   * `direction` is what we asked for, which is the right answer in that window.
   */
  #audioSender(call: MediaConnection): RTCRtpSender | null {
    const pc = call.peerConnection as RTCPeerConnection | undefined;
    if (!pc) return null;
    for (const transceiver of pc.getTransceivers()) {
      const direction = transceiver.currentDirection ?? transceiver.direction;
      if (direction === 'sendrecv' || direction === 'sendonly') return transceiver.sender;
    }
    return null;
  }

  /**
   * Place the voice call to a peer, if we are the end that should.
   *
   * One MediaConnection carries both directions (see `#onIncomingCall`), so
   * exactly one end must dial: the lower peer ID does it, the same arbitrary
   * tie-break `session.ts` uses for data connections, agreed on by both sides
   * without exchanging a message. Every peer attaches a stream at join, so the
   * designated end can always dial — there is no case to fall back for.
   */
  #offerVoice(peerId: PeerId): void {
    const record = this.#records.get(peerId);
    if (!record || record.media || !this.#outboundStream) return;
    if (this.#selfId === null || this.#selfId > peerId) return;
    this.#scheduleCall(peerId, 0);
  }

  /** Every dial is deferred, so the conditions are re-checked when it fires. */
  #scheduleCall(peerId: PeerId, delayMs: number): void {
    setTimeout(() => this.#callPeer(peerId), delayMs);
  }

  #callPeer(peerId: PeerId): void {
    const peer = this.#peer;
    const record = this.#records.get(peerId);
    if (this.#closed || !peer || !record || record.media) return;
    const stream = this.#outboundStream;
    if (!stream || record.callAttempts >= MEDIA_CALL_ATTEMPTS) return;
    record.callAttempts += 1;
    const call = peer.call(peerId, stream);
    record.media = call;
    record.mediaStream = stream;
    this.#adoptCall(record, call);
    // The call was set up with the placeholder; put the microphone in if one is
    // already open. A no-op when muted, which is the usual case.
    this.#applyTrack(record);
  }

  /** Both ends of a call want the same handlers, whoever placed it. */
  #adoptCall(record: PeerRecord, call: MediaConnection): void {
    call.on('stream', (stream) => {
      // Audio is flowing, so whatever the earlier attempts were about is over.
      record.callAttempts = 0;
      this.#stream.emit(call.peer, stream);
    });
    call.on('close', () => {
      if (record.media !== call) return;
      record.media = null;
      record.mediaStream = null;
      // Either the deliberate teardown from `attachStream` or a call that
      // dropped. Both want the same thing: form it again. `callAttempts` is what
      // stops a call that will never succeed from retrying forever.
      if (!this.#closed) setTimeout(() => this.#offerVoice(call.peer), MEDIA_REDIAL_MS);
    });
    call.on('error', (err: PeerJsError) =>
      this.#error.emit({
        code: err?.type ?? 'media-error',
        message: err?.message ?? String(err),
        fatal: false,
        peerId: call.peer,
      }),
    );
  }

  describeVoice(): VoiceDiagnostics[] {
    return [...this.#records.values()].map((record) => {
      const call = record.media;
      const pc = (call?.peerConnection ?? null) as RTCPeerConnection | null;
      const sender = call ? this.#audioSender(call) : null;
      return {
        peerId: record.info.id,
        hasCall: call !== null,
        open: call?.open ?? false,
        hasSender: sender !== null,
        senderTrack: sender?.track?.readyState ?? null,
        direction:
          (pc?.getTransceivers() ?? [])
            .map((t) => t.currentDirection ?? t.direction)
            .join(',') || null,
        receiving: (pc?.getReceivers() ?? []).some((r) => r.track?.readyState === 'live'),
        connectionState: pc?.connectionState ?? null,
        callAttempts: record.callAttempts,
      };
    });
  }

  #targets(to?: PeerId): PeerRecord[] {
    if (to === undefined) return [...this.#records.values()];
    const record = this.#records.get(to);
    return record ? [record] : [];
  }

  // -- subscriptions --------------------------------------------------------

  onPeerJoin(cb: (peer: PeerInfo) => void): Unsubscribe {
    return this.#peerJoin.on(cb);
  }
  onPeerLeave(cb: (peerId: PeerId, reason: string) => void): Unsubscribe {
    return this.#peerLeave.on(cb);
  }
  onInput(cb: (from: PeerId, payload: Uint8Array, receivedAt: number) => void): Unsubscribe {
    return this.#input.on(cb);
  }
  onControl(cb: (from: PeerId, msg: ControlMessage) => void): Unsubscribe {
    return this.#control.on(cb);
  }
  onStream(cb: (from: PeerId, stream: MediaStream) => void): Unsubscribe {
    return this.#stream.on(cb);
  }
  onStatus(cb: (status: TransportStatus, detail: string) => void): Unsubscribe {
    return this.#status.on(cb);
  }
  onError(cb: (err: TransportError) => void): Unsubscribe {
    return this.#error.on(cb);
  }

  // -- introspection --------------------------------------------------------

  getPeers(): PeerInfo[] {
    return [...this.#records.values()].filter((r) => r.announced).map((r) => r.info);
  }

  describeChannels(): ChannelDiagnostics[] {
    const out: ChannelDiagnostics[] = [];
    for (const record of this.#records.values()) {
      if (record.control) out.push(describeChannel(record.info.id, 'control', record.control, true, CONTROL_SERIALIZATION));
      if (record.input) out.push(describeChannel(record.info.id, 'input', record.input, false, INPUT_SERIALIZATION));
    }
    return out;
  }

  async getPeerStats(peerId: PeerId): Promise<PeerStats | null> {
    const pc = this.#records.get(peerId)?.control?.peerConnection;
    if (!pc) return null;
    let report: RTCStatsReport;
    try {
      report = await pc.getStats();
    } catch {
      return null;
    }
    return summarizeStats(peerId, report);
  }
}

// -- helpers ----------------------------------------------------------------

class TransportFailure extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(`${code}: ${message}`);
    this.name = 'TransportFailure';
    this.code = code;
  }
}

function isUnavailableId(err: unknown): boolean {
  return err instanceof TransportFailure && err.code === 'unavailable-id';
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Only include keys the caller actually set. PeerJS falls back to its cloud
 * broker defaults for anything absent, and passing `undefined` explicitly is
 * not the same thing as omitting it.
 */
function buildPeerOptions(broker: BrokerConfig): PeerOptions {
  const options: PeerOptions = {};
  if (broker.host !== undefined) options.host = broker.host;
  if (broker.port !== undefined) options.port = broker.port;
  if (broker.path !== undefined) options.path = broker.path;
  if (broker.secure !== undefined) options.secure = broker.secure;
  if (broker.key !== undefined) options.key = broker.key;
  if (broker.debugLevel !== undefined) options.debug = broker.debugLevel;
  if (broker.iceServers?.length) options.config = { iceServers: broker.iceServers };
  return options;
}

function toBytes(data: unknown): Uint8Array | null {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  return null;
}

function describeType(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value !== 'object') return typeof value;
  return value.constructor?.name ?? 'object';
}

function describeChannel(
  peerId: PeerId,
  kind: 'control' | 'input',
  conn: DataConnection,
  requestedReliable: boolean,
  requestedSerialization: string,
): ChannelDiagnostics {
  const dc = conn.dataChannel as RTCDataChannel | null | undefined;
  return {
    peerId,
    kind,
    label: conn.label,
    requestedReliable,
    requestedSerialization,
    readyState: dc?.readyState ?? 'missing',
    ordered: dc ? dc.ordered : null,
    maxRetransmits: dc ? dc.maxRetransmits : null,
    maxPacketLifeTime: dc ? dc.maxPacketLifeTime : null,
    negotiated: dc ? dc.negotiated : null,
    id: dc ? dc.id : null,
    binaryType: dc ? dc.binaryType : null,
  };
}

function summarizeStats(peerId: PeerId, report: RTCStatsReport): PeerStats {
  const byId = new Map<string, Record<string, unknown>>();
  let pair: Record<string, unknown> | null = null;
  let transportSelectedPairId: string | null = null;

  report.forEach((entry) => {
    const stat = entry as unknown as Record<string, unknown>;
    byId.set(String(stat['id']), stat);
    if (stat['type'] === 'transport' && typeof stat['selectedCandidatePairId'] === 'string') {
      transportSelectedPairId = stat['selectedCandidatePairId'];
    }
  });

  if (transportSelectedPairId) pair = byId.get(transportSelectedPairId) ?? null;
  if (!pair) {
    // Firefox does not always expose transport.selectedCandidatePairId.
    for (const stat of byId.values()) {
      if (stat['type'] === 'candidate-pair' && (stat['nominated'] === true || stat['selected'] === true) && stat['state'] === 'succeeded') {
        pair = stat;
        break;
      }
    }
  }

  const local = pair && typeof pair['localCandidateId'] === 'string' ? byId.get(pair['localCandidateId']) : undefined;
  const remote = pair && typeof pair['remoteCandidateId'] === 'string' ? byId.get(pair['remoteCandidateId']) : undefined;
  const localType = typeof local?.['candidateType'] === 'string' ? (local['candidateType'] as string) : null;
  const remoteType = typeof remote?.['candidateType'] === 'string' ? (remote['candidateType'] as string) : null;
  const rttSeconds = pair && typeof pair['currentRoundTripTime'] === 'number' ? pair['currentRoundTripTime'] : null;

  return {
    peerId,
    rttMs: rttSeconds === null ? null : Math.round(rttSeconds * 10_000) / 10,
    localCandidateType: localType,
    remoteCandidateType: remoteType,
    relayed: localType === 'relay' || remoteType === 'relay',
    availableOutgoingBitrateBps:
      pair && typeof pair['availableOutgoingBitrate'] === 'number' ? pair['availableOutgoingBitrate'] : null,
    bytesSent: pair && typeof pair['bytesSent'] === 'number' ? pair['bytesSent'] : null,
    bytesReceived: pair && typeof pair['bytesReceived'] === 'number' ? pair['bytesReceived'] : null,
  };
}
